import { UiCommand, UiCommandEvent } from '@ui/cer-ui-types';
import { CerUiIcons } from '@ui/cer-ui-icons';
import { CerFormAdapter } from '@form/cer-form-adapter';
import { CerFormViewSet } from '@form/cer-form-view-set';
import { CerFormView, ICerViewComponent } from '@form/cer-form-view';
import { CerFormPanesSettingsUtil, uiCmdPaneRightToggle } from '@form/cer-form-panes/cer-form-panes.service';
import { FieldWidthEnum } from '@data/cer-data-types';

// Api
import { UserDto, UserClient, UserRoleClient, UserRoleVm } from '../../app/api';

// Dialog forms
import { UserIdentityComponent } from './user-identity/user-identity.component';
import { UserRoleComponent } from './user-role/user-role.component';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';

// Ui commands
const uiCmdUserRoleShowHide = Object.assign(new Object(), uiCmdPaneRightToggle, { id: 'UserRoleShowHide', text: 'Vis roller', toolbarShowTextOn: "Overflow", tooltipText: 'Vis liste over roller for brugeren' });
const uiCmdUserRoleEdit: UiCommand = { id: 'UserRoleEdit', text: 'Tilknyt roller', iconCss: CerUiIcons.lock, tooltipText: 'Tilknyt eller fjern roller fra brugeren', target: ".e-content", needSelection: true };
const uiCmdUserLinkLogin: UiCommand = { id: 'UserLinkLogin', text: 'Tilknyt login', iconCss: CerUiIcons.password, tooltipText: 'Tilknyt login til brugeren', target: ".e-content", needSelection: true };

export class UserForm extends CerFormAdapter {

  public override viewSetInit(viewSet: CerFormViewSet): void {
    const simpleMode = this.routeOptions.routeParams?.includes("simpleMode");

    const user = this.getViewUser(simpleMode);
    viewSet.viewAdd(user);

    if (!simpleMode) {
      viewSet.panes = CerFormPanesSettingsUtil.masterDetail('horizontal', false, 38);
      viewSet.viewAdd(this.getViewUserRole(), user);
    }
  }

  private getViewUser(simpleMode: boolean): CerFormView {
    var v = new CerFormView();

    v.onCommandEvent = (e => this.onCommand(e));
    v.viewMetadata = {
      name: 'brugere', dataApiName: 'User',
      dataApiParams: ['$filterSystemRows'], text: 'Brugere', textSingular: 'Bruger',
      primaryKey: 'id', titleFields: ['shortName'], baseFields: ['id', 'history']
    };
    v.fieldMetadata =
      [
        { name: 'shortName', text: 'Kort navn', tooltipText: 'Afsender af besked', required: true, allowEdit: false, allowEditOnAdd: true, widthType: FieldWidthEnum.M, orderBy: 'Ascending' },
        { name: 'name', text: 'Navn', tooltipText: 'Afsender af besked', required: true, allowEdit: true, allowEditOnAdd: true, widthType: FieldWidthEnum.L },
        { name: 'email', text: 'E-mail', tooltipText: 'Brugers e-mail', allowEdit: true, defaultValue: '', allowEditOnAdd: true, widthType: FieldWidthEnum.L }
      ];

    if (simpleMode) {
      const m = v.viewMetadata;
      m.allowEdit = false;
      m.allowCreate = false;
      m.allowDelete = false;
      m.name = 'medarbejdere'
      m.text = 'Medarbejdere';
      m.textSingular = 'Medarbejder';
    }
    else {
      v.toolbarCommands = [uiCmdUserRoleShowHide, uiCmdUserRoleEdit, uiCmdUserLinkLogin];
      v.contextMenuCommands = [uiCmdUserRoleShowHide, uiCmdUserRoleEdit, uiCmdUserLinkLogin];
      v.fieldMetadata = v.fieldMetadata.concat([
        { name: 'active', text: 'Aktiv', tooltipText: 'Er brugeren aktiv', allowEdit: true, allowEditOnAdd: true, format: 'CheckBox' },
        { name: 'locale', text: 'Sprog', tooltipText: 'Indstilling for sprog', visible: false, foreignKeyTableName: 'Locale', foreignKeyField: 'num', foreignKeyValue: 'name' },
        { name: 'appUserId', text: 'Login', tooltipText: 'Reference til login', allowEdit: false, allowEditOnAdd: true, visible: false, widthType: FieldWidthEnum.M },
        { name: 'uiPagerShow', text: 'Sideopdel lister', tooltipText: 'Skal lister vises sideopdelt?', allowEdit: true, allowEditOnAdd: true, visible: true, format: 'CheckBox' }
      ]);
    }

    return v;
  }

  private getViewUserRole(): CerFormView {
    var v = new CerFormView();
    v.mandatory = true;
    v.viewMetadata = {
      name: 'user-roles', dataApiName: 'RoleUserView', text: 'Roller', textSingular: 'Rolle tilknytning',
      primaryKey: 'id', parentPrimaryKey: 'id', parentForeignKey: 'userId', titleFields: ['roleName'], allowEdit: false, allowCreate: false, allowDelete: false, baseFields: ['history']
    };

    v.fieldMetadata =
      [{ name: 'id', text: 'Id', tooltipText: 'Unik nøgle for posten', allowEdit: false, visible: false, isIdentity: true, widthType: FieldWidthEnum.S },
      { name: 'roleId', text: 'Rolle Id', tooltipText: 'Rollens nøgle', allowEdit: false, widthType: FieldWidthEnum.S, visible: false },
      { name: 'roleName', text: 'Rollenavn', tooltipText: 'Navn på rollen', allowEdit: false, widthType: FieldWidthEnum.L, orderBy: 'Ascending' },
      { name: 'userId', text: 'Bruger Id', tooltipText: 'Brugerens nøgle', allowEdit: false, widthType: FieldWidthEnum.S, visible: false },
      { name: 'userName', text: 'Brugernavn', tooltipText: 'Brugerens navn', allowEdit: false, widthType: FieldWidthEnum.M, visible: false },
      { name: 'isActive', text: 'Aktiv', tooltipText: 'Er rollen aktiv?', allowEdit: false, widthType: FieldWidthEnum.XXXS, format: 'CheckBox' }
      ];

    return v;
  }

  // Commands
  private onCommand(event: UiCommandEvent) {
    switch (event.commandId) {
      case uiCmdUserRoleShowHide.id:
        this.userRoleShowHide(event.appComponent);
        break
      case uiCmdUserRoleEdit.id:
        this.userRoleEdit(event.appComponent);
        break;
      case uiCmdUserLinkLogin.id:
        this.userLinkLogin(event.appComponent);
        break;
    }
  }

  public userRoleShowHide(viewUi: ICerViewComponent) {
    if (this.context.form?.panesService) {
      this.context.form.panesService.toggle('right');
      if (this.context.form.panesService.visible('right')) {
        viewUi.rowSelectedRefresh();
      }
    }
  }

  public userRoleEdit(viewUi: ICerViewComponent) {
    var dto: UserDto = <UserDto>viewUi.rowSelectedData();
    if (dto && dto.id) {
      const userRoleClient = this.inject(UserRoleClient);
      userRoleClient.get(dto.id).subscribe(vm => {
        this.userRoleDialog(vm, viewUi)
      });
    }
    else {
      this.context.ui.errorToast("Marker venligst en bruger");
    }
  }

  public userRoleDialog(vm: UserRoleVm, viewUi: ICerViewComponent) {
    this.context.ui.operationBegin();
    const config = { width: '800px', height: '600px', maxWidth: '95vw', data: { vm: vm } } as MatDialogConfig;
    const dialogRef = this.inject(MatDialog).open(UserRoleComponent, config) as  MatDialogRef<UserRoleComponent>;
    dialogRef.afterClosed().subscribe({
      next: (result) => {
        this.context.ui.operationEnd();
        if (result && result.dataChanged) {
          this.userRolePost(vm.user.id, result.data, viewUi);
        }
      },
      error: async (error) => {
        this.context.ui.operationEndError(error);
      }
    });
  }

  public userRolePost(userId: number, roleIds: number[], viewUi: ICerViewComponent) {
    this.context.routeService.setIsLoading(true);
    const userRoleClient = this.inject(UserRoleClient);
    userRoleClient.post(userId, roleIds).subscribe({
      next: (changesMade) => {
        this.context.routeService.setIsLoading(false);
        if (changesMade) {
          viewUi.research();
        }
      },
      error: async (error) => {
        this.context.routeService.setIsLoading(false);
        this.context.ui.errorApi(error);
      }
    }
    );
  }

  public userLinkLogin(viewUi: ICerViewComponent) {
    var dto: UserDto = <UserDto>viewUi.rowSelectedData();
    if (dto && dto.id) {
      const userRoleClient = this.inject(UserRoleClient);
      userRoleClient.get(dto.id).subscribe(vm => this.userIdentityDialog(vm, viewUi));
    }
    else {
      this.context.ui.errorToast("Marker venligst en bruger");
    }
  }

  public userIdentityDialog(vm: UserRoleVm, viewUi: ICerViewComponent) {
    this.context.routeService.setIsLoading(true);
    var config = <MatDialogConfig>{ width: '600px', height: '600px', maxWidth: '95vw', data: { vm: vm } };
    var dialogRef: MatDialogRef<UserIdentityComponent> = this.inject(MatDialog).open(UserIdentityComponent, config);
    dialogRef.afterClosed().subscribe(result => {
      this.context.routeService.setIsLoading(false);
      if (result && result.dataChanged && result.identityId) {
        this.linkUserIdentity(vm.user.id, result.identityId, result.confirmEmail, viewUi);
      }
    });
  }

  public linkUserIdentity(userId: number, identityId: string, confirmEmail: boolean, viewUi: ICerViewComponent) {
    this.context.routeService.setIsLoading(true);
    const userClient = this.inject(UserClient);
    userClient.linkUserIdentity(userId, identityId, confirmEmail).subscribe({
      next: (changesMade) => {
        this.context.routeService.setIsLoading(false);
        if (changesMade) {
          viewUi.research();
        }
      },
      error: async (error) => {
        this.context.routeService.setIsLoading(false);
        this.context.ui.errorApi(error);
      }
    }
    );
  }
}
